import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  getNotificationsApi,
  getUnreadCountApi,
  markNotificationReadApi,
  markAllNotificationsReadApi,
  deleteNotificationApi,
} from "../../api/notificationApi";

// ============================================
// ASYNC THUNKS
// ============================================

// ✅ Fetch Notifications
export const fetchNotifications = createAsyncThunk(
  'notification/fetchNotifications',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await getNotificationsApi(params);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch notifications');
    }
  }
);

// ✅ Get Unread Count
export const fetchUnreadCount = createAsyncThunk(
  'notification/fetchUnreadCount',
  async (role = null, { rejectWithValue }) => {
    try {
      const response = await getUnreadCountApi(role);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to get unread count');
    }
  }
);

// ✅ Mark Notification as Read
export const markNotificationRead = createAsyncThunk(
  'notification/markNotificationRead',
  async (notificationId, { rejectWithValue }) => {
    try {
      const response = await markNotificationReadApi(notificationId);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to mark notification as read');
    }
  }
);

// ✅ Mark All Notifications as Read
export const markAllNotificationsRead = createAsyncThunk(
  'notification/markAllNotificationsRead',
  async (role = null, { rejectWithValue }) => {
    try {
      const response = await markAllNotificationsReadApi(role);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to mark all as read');
    }
  }
);

// ✅ Delete Notification
export const deleteNotification = createAsyncThunk(
  'notification/deleteNotification',
  async (notificationId, { rejectWithValue }) => {
    try {
      const response = await deleteNotificationApi(notificationId);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete notification');
    }
  }
);

// ============================================
// INITIAL STATE
// ============================================
const initialState = {
  notifications: [],
  unreadCount: 0,
  total: 0,
  page: 1,
  totalPages: 1,
  loading: false,
  error: null,
  success: false,
  lastReceived: null,
};

// ============================================
// SLICE
// ============================================
const notificationSlice = createSlice({
  name: 'notification',
  initialState,
  reducers: {
    // ✅ Add notification from socket
    addNotification: (state, action) => {
      const notification = action.payload;
      state.notifications.unshift(notification);
      state.total += 1;
      state.unreadCount += 1;
      state.lastReceived = new Date();
    },
    
    // ✅ Mark as read (local)
    markAsRead: (state, action) => {
      const notificationId = action.payload;
      const notification = state.notifications.find(n => n._id === notificationId);
      if (notification && !notification.read) {
        notification.read = true;
        notification.readAt = new Date();
        state.unreadCount = Math.max(0, state.unreadCount - 1);
      }
    },
    
    // ✅ Mark all as read (local)
    markAllAsRead: (state) => {
      state.notifications.forEach(n => {
        n.read = true;
        n.readAt = new Date();
      });
      state.unreadCount = 0;
    },
    
    // ✅ Remove notification
    removeNotification: (state, action) => {
      const notificationId = action.payload;
      const notification = state.notifications.find(n => n._id === notificationId);
      if (notification && !notification.read) {
        state.unreadCount = Math.max(0, state.unreadCount - 1);
      }
      state.notifications = state.notifications.filter(n => n._id !== notificationId);
      state.total = Math.max(0, state.total - 1);
    },
    
    // ✅ Clear all notifications
    clearNotifications: (state) => {
      state.notifications = [];
      state.unreadCount = 0;
      state.total = 0;
    },
    
    // ✅ Set unread count from socket
    setUnreadCount: (state, action) => {
      state.unreadCount = action.payload;
    },
    
    // ✅ Reset state
    resetNotificationState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      // ============================================
      // FETCH NOTIFICATIONS
      // ============================================
      .addCase(fetchNotifications.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchNotifications.fulfilled, (state, action) => {
        state.loading = false;
        state.notifications = action.payload.data || [];
        state.unreadCount = action.payload.unread || 0;
        state.total = action.payload.pagination?.total || 0;
        state.page = action.payload.pagination?.page || 1;
        state.totalPages = action.payload.pagination?.totalPages || 1;
        state.error = null;
      })
      .addCase(fetchNotifications.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch notifications';
      })

      // ============================================
      // FETCH UNREAD COUNT
      // ============================================
      .addCase(fetchUnreadCount.fulfilled, (state, action) => {
        state.unreadCount = action.payload.count || 0;
      })
      .addCase(fetchUnreadCount.rejected, (state, action) => {
        console.error('Fetch unread count error:', action.payload);
      })

      // ============================================
      // MARK NOTIFICATION AS READ
      // ============================================
      .addCase(markNotificationRead.pending, (state) => {
        state.loading = true;
      })
      .addCase(markNotificationRead.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        
        const notification = state.notifications.find(
          n => n._id === action.payload.data?._id
        );
        if (notification && !notification.read) {
          notification.read = true;
          notification.readAt = new Date();
          state.unreadCount = Math.max(0, state.unreadCount - 1);
        }
        state.error = null;
      })
      .addCase(markNotificationRead.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to mark as read';
      })

      // ============================================
      // MARK ALL NOTIFICATIONS AS READ
      // ============================================
      .addCase(markAllNotificationsRead.pending, (state) => {
        state.loading = true;
      })
      .addCase(markAllNotificationsRead.fulfilled, (state) => {
        state.loading = false;
        state.success = true;
        state.notifications.forEach(n => {
          n.read = true;
          n.readAt = new Date();
        });
        state.unreadCount = 0;
        state.error = null;
      })
      .addCase(markAllNotificationsRead.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to mark all as read';
      })

      // ============================================
      // DELETE NOTIFICATION
      // ============================================
      .addCase(deleteNotification.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteNotification.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        
        const notificationId = action.meta.arg;
        const notification = state.notifications.find(n => n._id === notificationId);
        if (notification && !notification.read) {
          state.unreadCount = Math.max(0, state.unreadCount - 1);
        }
        state.notifications = state.notifications.filter(n => n._id !== notificationId);
        state.total = Math.max(0, state.total - 1);
        state.error = null;
      })
      .addCase(deleteNotification.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to delete notification';
      });
  },
});

// ============================================
// ACTIONS
// ============================================
export const {
  addNotification,
  markAsRead,
  markAllAsRead,
  removeNotification,
  clearNotifications,
  setUnreadCount,
  resetNotificationState,
} = notificationSlice.actions;

// ============================================
// SELECTORS
// ============================================
export const selectNotifications = (state) => state.notification.notifications;
export const selectUnreadCount = (state) => state.notification.unreadCount;
export const selectNotificationLoading = (state) => state.notification.loading;
export const selectNotificationError = (state) => state.notification.error;
export const selectNotificationSuccess = (state) => state.notification.success;
export const selectNotificationTotal = (state) => state.notification.total;

// ============================================
// EXPORT DEFAULT
// ============================================
export default notificationSlice.reducer;